// Reading plans (approved by Eddy, 2026-10-09; biblestandard-api docs/fellowship/08-READING-PLANS.md):
// start a plan or make one, read alone or together with friends and anyone you invite, track
// progress, talk over each day.
//
// The shapes are the API's (F5): a plan is a `sequence` (day keys `day-<n>`) or a `calendar` (keys
// `MM-DD`, a perpetual table such as M'Cheyne's), each day a heading and references (OSIS book,
// chapter and verse range, verse 0 = the chapter's own edge). A reader follows a calendar plan
// `byDate` (the day for 9 October is 9 October's) or `fromDayOne`. Scripture text never comes with a
// plan: the passage page reads it from the WEB (bible.ts).
//
// Our rules on top (08 §1):
// - Presence, not competition: who has read today, never a leaderboard, percentage or streak.
// - Behind is normal: "start from today" or "mark earlier read", never a count of missed days.
// - Rest on the Lord's Day: a plan can leave Sundays free, and its dates skip them.
// - A shared plan is its own small room: members see each other's progress in that plan only.
import { localDate } from "./api";
import { CHAPTERS } from "./study";
import type { UserCard } from "./types";
import type { SectionReading } from "./works";

/** A Scripture reading, as a reference: `key` is OSIS ("John.3", "Rom.1-Rom.2", "Gen.1.20-Gen.2.4"). */
export type ScriptureReading = { kind: "scripture"; key: string; label: string; book: string; chStart: number; vStart: number; chEnd: number; vEnd: number };
/**
 * One reading (the API's F7 PlanReading): a Scripture range, or a section of a book in the app's
 * library (`{libraryId, sectionUid}`, keyed `b:<libraryId>/<sectionUid>`; 09-BOOKS-AND-AUTHORS §4.2).
 */
export type Reading = ScriptureReading | SectionReading;
export type { SectionReading };
/** The work read alongside a plan, as a reference into the library (display only; its sections are in the days). */
export type CompanionWork = { libraryId: string; title: string; author: string };
/** One day: its key (`day-<n>` or `MM-DD`), its heading ("Day 4", "1 January"), its readings. */
export type PlanDay = { key: string; heading: string; readings: Reading[] };
export type ScheduleKind = "sequence" | "calendar";
export type FollowMode = "fromDayOne" | "byDate";

export type PlanSummary = {
  id: string;
  /** A plan from Bible Standard's catalogue (M'Cheyne, the 1662 lectionary), not a reader's. */
  classic: boolean;
  title: string;
  description: string | null;
  /** The reader who made it; null for the classic plans. */
  author: UserCard | null;
  days: number;
  books: string[] | null;
  /** For display: "Title · Author" of the work read alongside, if any (or an older free-text companion). */
  companion: string | null;
  /** The work read alongside, as a reference into the library. */
  companionWork: CompanionWork | null;
  restSundays: boolean;
  visibility: "public" | "friends" | "private";
  scheduleKind: ScheduleKind;
  packageId: string | null;
  /** One question for each day's conversation; cycles through the plan. */
  prompts: string[];
  createdAt: string;
};
export type Plan = PlanSummary & { schedule: PlanDay[] };

export type Member = { user: UserCard; read: number[]; joinedAt: string | null; isMe: boolean };

export type Journey = {
  id: string;
  /** With its schedule from journey(id) and the writes; perhaps without it in journeys(). */
  plan: PlanSummary & { schedule?: PlanDay[] };
  startedOn: string;
  together: boolean;
  follow: FollowMode;
  inviteCode: string;
  /** The web path of the invite link, /plans/join/<code>. */
  invitePath: string;
  createdAt: string;
  /**
   * The reader's own progress, and their place today (the API's placeOn, for the reader's local date):
   * `day` is today's plan day, `first` the day their reading counts from, `today` that day's readings.
   */
  me: { paused: boolean; startOffset: number; read: number[]; joinedAt: string | null; day: number; first: number; today: PlanDay | null };
  /** Everyone reading, the reader included (isMe); only the reader in a plan read alone. */
  members: Member[];
  invited: UserCard[];
};

export type DayNote = { id: string; journeyId: string; day: number; author: UserCard; body: string; createdAt: string; hidden: boolean; canDelete: boolean };

export type Invitation = { journeyId: string; from: UserCard | null; plan: PlanSummary; startedOn: string; members: UserCard[]; invitedAt: string | null };

export type NewPlan = {
  title: string;
  description: string;
  /** Books of the Bible; may be empty only when a work is read alongside. */
  books: string[];
  days: number;
  restSundays: boolean;
  visibility: "friends" | "private";
  /** A work from the library read alongside: its sections given (contents order), or all of them. */
  alongside?: { libraryId: string; sectionUids?: string[] };
};

export type StartPlan = { together: boolean; invite: string[]; startOn: string; follow?: FollowMode };

export interface PlanSource {
  plans(): Promise<PlanSummary[]>;
  plan(id: string): Promise<Plan | null>;
  createPlan(input: NewPlan): Promise<Plan>;
  journeys(): Promise<Journey[]>;
  journey(id: string): Promise<Journey | null>;
  start(planId: string, opts: StartPlan): Promise<Journey>;
  setRead(journeyId: string, day: number, read: boolean): Promise<Journey>;
  setPaused(journeyId: string, paused: boolean): Promise<Journey>;
  /** `today` is the reader's local date: "before today" is theirs, not UTC's. */
  catchUp(journeyId: string, how: "mark-read" | "start-from-today", today: string): Promise<Journey>;
  invite(journeyId: string, userIds: string[]): Promise<void>;
  withdrawInvite(journeyId: string, userId: string): Promise<void>;
  /** From an invite link, /plans/join/<code>. */
  join(code: string): Promise<Journey>;
  leave(journeyId: string): Promise<void>;
  invitations(): Promise<Invitation[]>;
  respond(journeyId: string, accept: boolean): Promise<Journey | null>;
  notes(journeyId: string, day: number): Promise<{ prompt: string; items: DayNote[] }>;
  addNote(journeyId: string, day: number, body: string): Promise<DayNote>;
  deleteNote(journeyId: string, noteId: string): Promise<void>;
}

// ---- Making a plan ----

/** Readable chapter runs: [["Rom",1],["Rom",2],["1Cor",1]] → Romans 1–2, 1 Corinthians 1. As the API's. */
function runs(chs: [string, number][]): ScriptureReading[] {
  const out: ScriptureReading[] = [];
  let i = 0;
  while (i < chs.length) {
    const [book, first] = chs[i]!;
    let last = first;
    while (i + 1 < chs.length && chs[i + 1]![0] === book && chs[i + 1]![1] === last + 1) {
      i++;
      last = chs[i]![1];
    }
    const name = CHAPTERS.find(([id]) => id === book)![1];
    const range = { kind: "scripture" as const, book, chStart: first, vStart: 0, chEnd: last, vEnd: 0 };
    out.push(
      first === last
        ? { key: `${book}.${first}`, label: `${name} ${first}`, ...range }
        : { key: `${book}.${first}-${book}.${last}`, label: `${name} ${first}–${last}`, ...range },
    );
    i++;
  }
  return out;
}

/**
 * Spread the chosen books' chapters evenly over the days, in canonical order. Never more days than
 * chapters. A book read alongside (`sections`, in contents order) is spread over the same days the
 * same way, after each day's Scripture; the plan then runs as long as the longer of the two, never
 * more than `days`. The API's buildSchedule, line for line.
 */
export function buildSchedule(books: string[], days: number, sections: SectionReading[] = []): PlanDay[] {
  const order = CHAPTERS.map(([id]) => id);
  const chapters: [string, number][] = [...new Set(books)]
    .filter((b) => order.includes(b))
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .flatMap((b) => Array.from({ length: CHAPTERS.find(([id]) => id === b)![2] }, (_, i) => [b, i + 1] as [string, number]));
  const n = Math.max(1, Math.min(days, Math.max(chapters.length, sections.length)));
  const slice = <T,>(xs: T[], d: number) => xs.slice(Math.floor((d * xs.length) / n), Math.floor(((d + 1) * xs.length) / n));
  // A book alongside begins on day 1 (rounding up), so fewer sections than days don't start late.
  const early = <T,>(xs: T[], d: number) => xs.slice(Math.ceil((d * xs.length) / n), Math.ceil(((d + 1) * xs.length) / n));
  return Array.from({ length: n }, (_, d) => ({
    key: `day-${d + 1}`,
    heading: `Day ${d + 1}`,
    readings: [...runs(slice(chapters, d)), ...early(sections, d)],
  }));
}

export function chapterCount(books: string[]) {
  return books.reduce((sum, b) => sum + (CHAPTERS.find(([id]) => id === b)?.[2] ?? 0), 0);
}

export const readingsLine = (day: PlanDay | undefined) => day?.readings.map((r) => r.label).join(", ") ?? "";

/** Readings stored before F7 have no `kind`: every one of them is Scripture. As the API's normaliseSchedule. */
export function normaliseSchedule(days: { key: string; heading: string; readings: (Reading | Omit<ScriptureReading, "kind">)[] }[]): PlanDay[] {
  return days.map((d) => ({ ...d, readings: d.readings.map((r) => ("kind" in r && r.kind ? r : ({ kind: "scripture", ...r } as ScriptureReading))) }));
}

/** The Scripture readings of a day (a plan's books), leaving its book sections out. */
export const scriptureOf = (day: PlanDay | undefined) => (day?.readings ?? []).filter((r): r is ScriptureReading => r.kind === "scripture");

// ---- The calendar (local dates, YYYY-MM-DD) ----

const DAY_MS = 86_400_000;
const noon = (iso: string) => Date.parse(`${iso}T12:00:00Z`);
const isoOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const addDays = (iso: string, n: number) => isoOf(noon(iso) + n * DAY_MS);
/** A local date at noon UTC, for formatting in any time zone without slipping a day. */
export const atNoon = (iso: string) => new Date(noon(iso));

/** The date of slot n counted from the start, skipping Sundays when the plan rests on the Lord's Day. */
export function dateOfSlot(startedOn: string, slot: number, restSundays: boolean): string {
  const d = new Date(noon(startedOn));
  const next = () => d.setTime(d.getTime() + DAY_MS);
  const skipSunday = () => {
    if (restSundays) while (d.getUTCDay() === 0) next();
  };
  skipSunday();
  for (let n = 1; n < slot; n++) {
    next();
    skipSunday();
  }
  return isoOf(d.getTime());
}

/** The slot a date falls in from the start (0 before it); a Sunday in a resting plan belongs to the Monday after. The API's slotOn. */
export function slotOn(startedOn: string, date: string, restSundays: boolean): number {
  const start = noon(startedOn);
  const end = noon(date);
  if (end < start) return 0;
  const diff = Math.round((end - start) / DAY_MS);
  if (!restSundays) return diff + 1;
  let n = 0;
  const startDow = new Date(start).getUTCDay();
  const full = Math.floor(diff / 7);
  n += full * 6;
  for (let i = full * 7; i < diff; i++) if ((startDow + i) % 7 !== 0) n++;
  return n + 1;
}

/** By date: the day a calendar plan appoints to a date, or the last before it (29 February); day 1 if none. The API's appointedOrdinal. */
export function appointedOrdinal(dayKeys: string[], date: string): number {
  const key = date.slice(5, 10);
  const exact = dayKeys.indexOf(key);
  if (exact >= 0) return exact + 1;
  let last = -1;
  dayKeys.forEach((k, i) => {
    if (/^\d{2}-\d{2}$/.test(k) && k <= key) last = i;
  });
  return last >= 0 ? last + 1 : 1;
}

/** The finish date of a plan started on a date, from day 1. */
export function finishDate(startedOn: string, plan: Pick<PlanSummary, "days" | "restSundays">): string {
  return dateOfSlot(startedOn, plan.days, plan.restSundays);
}

// ---- Where a reader is in a journey ----

type Followed = Pick<Journey, "startedOn" | "follow"> & { me: Pick<Journey["me"], "startOffset" | "paused" | "read"> } & {
  plan: Pick<PlanSummary, "days" | "restSundays"> & { schedule?: PlanDay[] };
};

const wrap = (d: number, n: number) => ((((d - 1) % n) + n) % n) + 1;
const keysOf = (j: Followed) => j.plan.schedule?.map((d) => d.key) ?? [];

export type Place = { day: number; first: number; slot: number };

/**
 * Where a reader is on a local date: the API's placeOn, line for line. `day` is today's plan day
 * (1..days). `first` is the day their reading counts from: day 1 from Day 1; by date, the day
 * appointed when they started, moved by "start from today" (`startOffset` days on, wrapping past the
 * year's end). `slot` is unclamped.
 */
export function placeOn(o: { follow: FollowMode; keys: string[]; startedOn: string; today: string; restSundays: boolean; startOffset: number; days: number }): Place {
  if (o.follow === "byDate") {
    const day = appointedOrdinal(o.keys, o.today);
    const base = appointedOrdinal(o.keys, o.startedOn);
    return { day, slot: day, first: ((base - 1 + o.startOffset) % o.days) + 1 };
  }
  const slot = slotOn(o.startedOn, o.today, o.restSundays) - o.startOffset;
  return { day: Math.min(Math.max(slot, 1), o.days), slot, first: 1 };
}

const placeOf = (j: Followed, date: string) =>
  placeOn({ follow: j.follow, keys: keysOf(j), startedOn: j.startedOn, today: date, restSundays: j.plan.restSundays, startOffset: j.me.startOffset, days: j.plan.days });

/** The day the reader's reading counts from: day 1, or by date where they started (moved by "start from today"). */
export function firstDay(j: Followed): number {
  return placeOf(j, j.startedOn).first;
}

/** The reader's day for a date; null before the start. */
export function dayOn(j: Followed, date: string): number | null {
  if (date < j.startedOn) return null;
  const p = placeOf(j, date);
  if (j.follow !== "byDate" && slotOn(j.startedOn, date, j.plan.restSundays) === 0) return null;
  return p.day;
}

/** The date the reader reads a day on. By date: the day's own date, in the year's round that holds today. */
export function dateOfDay(j: Followed, day: number, today = localDate()): string {
  if (j.follow !== "byDate") return dateOfSlot(j.startedOn, day + j.me.startOffset, j.plan.restSundays);
  const keys = keysOf(j);
  const first = firstDay(j);
  const key = keys[day - 1] ?? j.startedOn.slice(5);
  // A round runs from the first day's date to the day before it, a year on.
  const anchor = keys[first - 1] ?? j.startedOn.slice(5);
  const thisYear = Number(today.slice(0, 4));
  const round = Math.max(Number(j.startedOn.slice(0, 4)), today.slice(5) >= anchor ? thisYear : thisYear - 1);
  const year = day >= first ? round : round + 1;
  const date = `${year}-${key}`;
  return isoOf(noon(date)) === date ? date : `${year}-02-28`;
}

/** The days in the order the reader reads them: 1…n, or by date from their first day round the year. */
export function readingOrder(j: Followed): number[] {
  const n = j.plan.days;
  const f = firstDay(j);
  return Array.from({ length: n }, (_, i) => (j.follow === "byDate" ? wrap(f + i, n) : i + 1));
}

/** The reader's place today with that day's readings, as the API sends it in `me` (the mock computes it the same way). */
export function placeToday(j: Followed, today = localDate()): Pick<Journey["me"], "day" | "first" | "today"> {
  const p = placeOf(j, today);
  return { day: p.day, first: p.first, today: j.plan.schedule?.[p.day - 1] ?? null };
}

/** Everything a page says about the reader's place: today's day, the next unread, behind or finished. Never a count. */
export function progress(j: Followed, today = localDate()) {
  const order = readingOrder(j);
  const pos = new Map(order.map((d, i) => [d, i]));
  const todayDay = dayOn(j, today);
  const read = new Set(j.me.read);
  const firstUnread = order.find((d) => !read.has(d)) ?? null;
  const started = todayDay !== null;
  return {
    started,
    /** The day the reader reads today (the first day, before the start). */
    today: todayDay ?? firstDay(j),
    firstUnread,
    finished: firstUnread === null,
    behind: started && !j.me.paused && firstUnread !== null && pos.get(firstUnread)! < pos.get(todayDay)!,
    order,
    /** Where a day falls in the reader's order, for "earlier" and "later" without wrapping round the year. */
    position: (d: number) => pos.get(d) ?? 0,
  };
}

/** The other readers in a journey (never the reader). */
export const othersIn = (j: Pick<Journey, "members">) => j.members.filter((m) => !m.isMe);

export const DEFAULT_PROMPTS = [
  "What stands out to you today?",
  "What does today’s reading show you of God?",
  "Where do you see Christ in it?",
  "What will you carry with you today?",
  "What question would you ask the writer?",
];

export const promptFor = (plan: Pick<PlanSummary, "prompts">, day: number) => {
  const p = plan.prompts.length ? plan.prompts : DEFAULT_PROMPTS;
  return p[(day - 1) % p.length]!;
};

/** What a plan is, in one line: its length and pace, and whose it is. */
export function planLine(p: Pick<PlanSummary, "scheduleKind" | "days" | "restSundays" | "author">) {
  const pace = p.scheduleKind === "calendar" ? `A reading for every date, ${p.days} days` : `${p.days} days`;
  const rest = p.restSundays ? ", resting on the Lord’s Day" : "";
  const by = p.author ? `, by ${p.author.displayName}` : "";
  return `${pace}${rest}${by}`;
}

export { localDate };
